/**
 * Admin Authentication API - Updated for Stytch
 * Handles admin login, session management, and authentication using Stytch B2B
 *
 * Updated: July 9, 2025
 * Purpose: Secure admin authentication with Stytch B2B integration
 *
 * CHANGES:
 * - Updated login to use Stytch authentication endpoints
 * - Added magic link authentication support
 * - Added password reset functionality
 * - Enhanced session validation with Stytch
 * - Updated token and user data handling
 */

import { getApiBaseUrl } from '../../utils/apiConfig';
import enhancedTokenManager from '../../utils/enhancedTokenManager';

const API_BASE_URL = getApiBaseUrl();

export class AdminAuthAPI {
  
  /**
   * Admin login with email and password (via Stytch)
   * @param {string} email - Admin email address
   * @param {string} password - Admin password
   * @returns {Promise<{access_token: string, admin_user: object, expires_in: number}>}
   */
  static async login(email, password) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      // Handle MFA-required branch
      if (data.mfa_required && data.member_authenticated === false) {
        return {
          mfaRequired: true,
          data
        };
      }

      // Store admin token and user data securely
      localStorage.setItem('admin_token', data.access_token);
      localStorage.setItem('admin_user', JSON.stringify(data.admin_user));
      localStorage.setItem('admin_token_expires', Date.now() + (data.expires_in * 1000));

      return {
        token: data.access_token,
        user: data.admin_user,
        permissions: data.admin_user.permissions,
        expires_in: data.expires_in
      };
    } catch (error) {
      console.error('Admin login error:', error);
      throw error;
    }
  }

  /**
   * Trigger admin MFA challenge (SMS/TOTP)
   */
  static async sendMfaChallenge(payload) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/auth/mfa/challenge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send MFA challenge');
      }
      return data;
    } catch (error) {
      console.error('Admin MFA challenge error:', error);
      throw error;
    }
  }

  /**
   * Verify admin MFA code and mint admin token
   */
  static async verifyMfa(payload) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/auth/mfa/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Invalid MFA code');
      }

      // Store admin token and user data securely
      localStorage.setItem('admin_token', data.access_token);
      localStorage.setItem('admin_user', JSON.stringify(data.admin_user));
      localStorage.setItem('admin_token_expires', Date.now() + (data.expires_in * 1000));

      return data;
    } catch (error) {
      console.error('Admin MFA verify error:', error);
      throw error;
    }
  }

  /**
   * Send magic link for admin authentication
   * @param {string} email - Admin email address
   * @returns {Promise<{success: boolean, message: string}>}
   */
  static async sendMagicLink(email) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/auth/magic-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send magic link');
      }

      return data;
    } catch (error) {
      console.error('Magic link error:', error);
      throw error;
    }
  }

  /**
   * Authenticate magic link token
   * @param {string} token - Magic link token from URL
   * @returns {Promise<{access_token: string, admin_user: object, expires_in: number}>}
   */
  static async authenticateMagicLink(token) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/auth/magic-link/authenticate?token=${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Magic link authentication failed');
      }

      // Store admin token and user data securely
      localStorage.setItem('admin_token', data.access_token);
      localStorage.setItem('admin_user', JSON.stringify(data.admin_user));
      localStorage.setItem('admin_token_expires', Date.now() + (data.expires_in * 1000));

      return {
        token: data.access_token,
        user: data.admin_user,
        permissions: data.admin_user.permissions,
        expires_in: data.expires_in
      };
    } catch (error) {
      console.error('Magic link authentication error:', error);
      throw error;
    }
  }

  /**
   * Send password reset email
   * @param {string} email - Admin email address
   * @returns {Promise<{success: boolean, message: string}>}
   */
  static async sendPasswordReset(email) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/auth/password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send password reset email');
      }

      return data;
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  }

  /**
   * Complete password setup from email token
   * @param {string} token - Password setup token from email
   * @param {string} password - New password to set
   * @returns {Promise<{success: boolean, access_token: string, admin_user: object, expires_in: number}>}
   */
  static async completePasswordSetup(token, password) {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/auth/complete-password-setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Password setup failed');
      }

      return data;
    } catch (error) {
      console.error('Password setup error:', error);
      throw error;
    }
  }

  /**
   * Admin logout - invalidate Stytch session
   */
  static async logout() {
    try {
      const token = this.getToken();
      if (token) {
        await fetch(`${API_BASE_URL}/admin/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear admin session data
      this.clearSession();
    }
  }

  /**
   * Clear admin session data
   */
  static clearSession() {
    // Use enhancedTokenManager to clear admin token
    enhancedTokenManager.clearToken('admin_token');

    // Clean up any legacy localStorage entries
    localStorage.removeItem('admin_user');
    localStorage.removeItem('admin_token_expires');
  }

  /**
   * Get current admin token
   * @returns {string|null} Admin JWT token
   */
  static getToken() {
    // Admin tokens are stored directly in localStorage (not enhancedTokenManager)
    // enhancedTokenManager explicitly blocks admin_token by design
    try {
      const token = localStorage.getItem('admin_token');
      console.log('[AdminAuthAPI.getToken] Token from localStorage:', token ? 'EXISTS' : 'NULL');
      return token;
    } catch (error) {
      console.error('Error getting admin token:', error);
      return null;
    }
  }

  /**
   * Get current admin user
   * @returns {object|null} Admin user object
   */
  static getUser() {
    const user = localStorage.getItem('admin_user');
    return user ? JSON.parse(user) : null;
  }

  /**
   * Get current admin permissions
   * @returns {object|null} Admin permissions object
   */
  static getPermissions() {
    const user = this.getUser();
    return user ? user.permissions : null;
  }

  /**
   * Check if admin is authenticated
   * @returns {boolean} True if admin is logged in
   */
  static isAuthenticated() {
    const token = this.getToken();
    const user = this.getUser();
    return !!(token && user);
  }

  /**
   * Check if admin has specific permission
   * @param {string} permission - Permission to check
   * @returns {boolean} True if admin has permission
   */
  static hasPermission(permission) {
    const permissions = this.getPermissions();
    const user = this.getUser();
    
    // System admins have all permissions
    if (user && user.role === 'system_admin') {
      return true;
    }
    
    return permissions ? permissions[permission] === true : false;
  }

  /**
   * Check if admin has specific role
   * @param {string} role - Role to check
   * @returns {boolean} True if admin has role
   */
  static hasRole(role) {
    const user = this.getUser();
    if (!user) return false;
    
    const userRole = user.role.toLowerCase();
    const requiredRole = role.toLowerCase();
    
    // System admins have all roles
    if (userRole === 'system_admin') {
      return true;
    }
    
    // Check role hierarchy
    const roleHierarchy = {
      'admin': ['admin'],
      'super_admin': ['admin', 'super_admin'],
      'system_admin': ['admin', 'super_admin', 'system_admin']
    };
    
    const allowedRoles = roleHierarchy[userRole] || [];
    return allowedRoles.includes(requiredRole);
  }

  /**
   * Validate current admin session with server
   * @returns {Promise<boolean>} True if session is valid
   */
  static async validateSession() {
    try {
      const token = this.getToken();
      if (!token) return false;

      const response = await fetch(`${API_BASE_URL}/admin/auth/validate`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        // Session invalid, clear stored data
        this.clearSession();
        return false;
      }

      const data = await response.json();

      // Update user data if successful
      if (data && typeof data === 'object') {
        localStorage.setItem('admin_user', JSON.stringify(data));
      }

      return true;
    } catch (error) {
      console.error('Session validation error:', error);
      this.clearSession();
      return false;
    }
  }

  /**
   * Get authenticated fetch headers for admin API calls
   * @returns {object} Headers with authorization
   */
  static getAuthHeaders() {
    const token = this.getToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  /**
   * Make authenticated admin API request
   * @param {string} endpoint - API endpoint
   * @param {object} options - Fetch options
   * @returns {Promise<Response>} Fetch response
   */
  static async authenticatedRequest(endpoint, options = {}) {
    const token = this.getToken();
    if (!token) {
      throw new Error('No admin authentication token available');
    }

    const defaultOptions = {
      headers: this.getAuthHeaders(),
      ...options,
    };

    // Merge headers if options.headers exists
    if (options.headers) {
      defaultOptions.headers = {
        ...defaultOptions.headers,
        ...options.headers
      };
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, defaultOptions);

    // Handle session expiration
    if (response.status === 401) {
      this.clearSession();
      // Redirect to admin login
      window.location.href = '/admin/login';
      throw new Error('Admin session expired. Please login again.');
    }

    return response;
  }

  /**
   * Create a new admin user (system_admin only)
   * @param {object} userData - User data {email, password, name, role, permissions}
   * @returns {Promise<object>} Created user data
   */
  static async createAdminUser(userData) {
    try {
      const response = await this.authenticatedRequest('/admin/users', {
        method: 'POST',
        body: JSON.stringify(userData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create admin user');
      }

      return data;
    } catch (error) {
      console.error('Create admin user error:', error);
      throw error;
    }
  }

  /**
   * Get token expiration info
   * @returns {object} Token expiration info
   */
  static getTokenInfo() {
    const expires = localStorage.getItem('admin_token_expires');
    const token = localStorage.getItem('admin_token');
    
    if (!token || !expires) {
      return { valid: false, expired: true };
    }
    
    const expiresAt = parseInt(expires);
    const now = Date.now();
    const timeLeft = expiresAt - now;
    
    return {
      valid: timeLeft > 0,
      expired: timeLeft <= 0,
      expiresAt: new Date(expiresAt),
      timeLeft: Math.max(0, timeLeft),
      timeLeftMinutes: Math.max(0, Math.floor(timeLeft / (1000 * 60)))
    };
  }

  /**
   * Check if token is about to expire (within 5 minutes)
   * @returns {boolean} True if token expires soon
   */
  static isTokenExpiringSoon() {
    const tokenInfo = this.getTokenInfo();
    return tokenInfo.valid && tokenInfo.timeLeftMinutes <= 5;
  }
}

export default AdminAuthAPI; 
